import { ZoneId, ZonedDateTime } from "@js-joda/core";
import type { CompanyGigShift } from "model/Gig";
import { toOffsetDateTimeSafe } from "utils/date-time.logic";

export type PreparedShift = {
  shift: CompanyGigShift;
  originalIndex: number;
  startZdt: ZonedDateTime;
  endZdt: ZonedDateTime;
  startEpochMilli: number;
  endEpochMilli: number;
  dateEpochDay: number;
  startNanoOfDay: number;
  endNanoOfDay: number;
};

/** Returns the original indices for shifts that overlap with at least one other shift. */
export const getOverlappingShiftIndices = (
  preparedShifts: PreparedShift[],
): Set<number> => {
  const overlapping = new Set<number>();

  for (let i = 0; i < preparedShifts.length; i += 1) {
    for (let j = i + 1; j < preparedShifts.length; j += 1) {
      const a = preparedShifts[i];
      const b = preparedShifts[j];
      const overlaps =
        a.startEpochMilli < b.endEpochMilli &&
        b.startEpochMilli < a.endEpochMilli;

      if (overlaps) {
        overlapping.add(a.originalIndex);
        overlapping.add(b.originalIndex);
      }
    }
  }

  return overlapping;
};

/** Parses, filters invalid, and sorts shifts by local date, start, then end time. */
export const prepareSortedShifts = (
  shifts: CompanyGigShift[],
  zoneId: ZoneId,
): PreparedShift[] => {
  const prepared = shifts
    .map((shift, originalIndex) => {
      const startOffsetDateTime = toOffsetDateTimeSafe(shift.startTime);
      const endOffsetDateTime = toOffsetDateTimeSafe(shift.endTime);
      if (!startOffsetDateTime || !endOffsetDateTime) return null;
      const startZdt = startOffsetDateTime.atZoneSameInstant(zoneId);
      const endZdt = endOffsetDateTime.atZoneSameInstant(zoneId);

      return {
        shift,
        originalIndex,
        startZdt,
        endZdt,
        startEpochMilli: startOffsetDateTime.toInstant().toEpochMilli(),
        endEpochMilli: endOffsetDateTime.toInstant().toEpochMilli(),
        dateEpochDay: startZdt.toLocalDate().toEpochDay(),
        startNanoOfDay: startZdt.toLocalTime().toNanoOfDay(),
        endNanoOfDay: endZdt.toLocalTime().toNanoOfDay(),
      };
    })
    .filter((item): item is PreparedShift => item !== null);

  prepared.sort((a, b) => {
    if (a.dateEpochDay !== b.dateEpochDay) {
      return a.dateEpochDay - b.dateEpochDay;
    }

    if (a.startNanoOfDay !== b.startNanoOfDay) {
      return a.startNanoOfDay - b.startNanoOfDay;
    }

    if (a.endNanoOfDay !== b.endNanoOfDay) {
      return a.endNanoOfDay - b.endNanoOfDay;
    }

    return 0;
  });

  return prepared;
};
